import CloseIcon from '@mui/icons-material/Close';
import {
    Autocomplete,
    AutocompleteGetTagProps,
    AutocompleteRenderInputParams,
    Box,
    styled,
    TextField,
    TextFieldProps,
    Typography,
} from '@mui/material';
import { EMAIL_VALIDATION_REGEX } from 'constants/regex';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, KeyboardEvent, SyntheticEvent, useState } from 'react';
import { EColors, getColor } from 'theme';

const StyledTextField = styled(TextField)<TextFieldProps & { error: boolean }>(({ error }) => ({
    flex: 1,
    boxSizing: 'border-box',
    width: '100%',
    '& .MuiInputBase-root.MuiAutocomplete-inputRoot': {
        padding: '6.445px 16px',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: getColor(EColors.v3Background),
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
        borderRadius: '12px',
    },
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
        fontSize: '0.875rem',
        minWidth: 100,
    },
    '& .MuiFormLabel-root': {
        left: '2px',
        top: '-2px',
        fontSize: '0.875rem',
        color: getColor(EColors.pureBlack, 0.5),
    },
    '& .MuiFormLabel-root.MuiInputLabel-shrink': {
        left: 2,
        top: 2,
        color: getColor(EColors.pureBlack, 0.6),
    },
    '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
        borderColor: getColor(EColors.v3Dark) + ' !important',
    },
}));

const StyledTag = styled(Box)(({ theme }) => ({
    fontSize: '0.875rem',
    lineHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    backgroundColor: getColor(EColors.pureWhite),
    color: getColor(EColors.v3Dark),
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    '&.MuiAutocomplete-tag': {
        margin: 0,
        marginRight: theme.spacing(0.25),
    },
}));

type TagProps = ReturnType<AutocompleteGetTagProps> & {
    label: string;
    showDelete?: boolean;
};

function Tag(props: TagProps) {
    const { label, showDelete, onDelete, ...other } = props;
    return (
        <StyledTag {...other}>
            {label}
            {showDelete && (
                <CloseIcon
                    sx={{ color: getColor(EColors.v3Dark), cursor: 'pointer', fontSize: '.875rem' }}
                    onClick={onDelete}
                />
            )}
        </StyledTag>
    );
}

// Validate email format
const isValidEmail = (email: string) => EMAIL_VALIDATION_REGEX.test(email);

type MultiInputTextFieldProps = {
    values: string[];
    delimiter?: string;
    label?: string;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    required?: boolean;
    name?: string;
    showDelete?: boolean;
    alreadyAddedValues?: string[];
    onChange: (values: string[]) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    isValid?: (value: string) => boolean;
};

export const MultiInputTextField: React.FC<MultiInputTextFieldProps> = ({
    values,
    label = '',
    delimiter = ',',
    placeholder,
    disabled,
    required,
    error,
    showDelete = false,
    onChange,
    onBlur,
    isValid = isValidEmail,
    alreadyAddedValues = [],
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [inputValue, setInputValue] = useState('');

    /**
     * Validates a single value and shows error message if invalid
     * @returns true if valid, false otherwise
     */
    const validateValue = (value: string, checkInNewArray?: string[]): boolean => {
        if (!value.trim()) return false;

        if (!isValid(value)) {
            enqueueSnackbar(`${value} is not a valid email`, { variant: 'error' });
            return false;
        }

        const valueLower = value.toLowerCase();

        // Check for duplicates in current values
        if (values.some(v => v.toLowerCase() === valueLower)) {
            enqueueSnackbar(`${value} is already added`, { variant: 'error' });
            return false;
        }

        // Check for duplicates within the new array (for comma-separated input)
        if (checkInNewArray && checkInNewArray.filter(v => v.toLowerCase() === valueLower).length > 1) {
            enqueueSnackbar(`${value} is already added`, { variant: 'error' });
            return false;
        }

        // Check for duplicates in already added values
        if (alreadyAddedValues?.some(v => v.toLowerCase() === valueLower)) {
            enqueueSnackbar(`${value} is already added`, { variant: 'error' });
            return false;
        }

        return true;
    };

    /**
     * Validates and adds a single value to the values array
     * @returns true if the value was added, false otherwise
     */
    const validateAndAddValue = (value: string): boolean => {
        if (!validateValue(value)) return false;

        onChange([...values, value]);
        return true;
    };

    const handleChange = (_event: SyntheticEvent, newValue: string[]): void => {
        // Allow removals without validation
        if (newValue.length < values.length) {
            onChange(newValue);
            return;
        }

        // Validate newly added values
        if (newValue.length > values.length) {
            const addedValues = newValue.filter(v => !values.includes(v));
            const hasInvalid = addedValues.some(value => !validateValue(value, newValue));

            if (hasInvalid) return; // Reject the change if any value is invalid
        }

        onChange(newValue);
    };

    const handleInputValueChange = (_event: SyntheticEvent, newInputValue: string): void => {
        setInputValue(newInputValue);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;

        if (!value || !value.includes(delimiter)) return;

        const splitValues = value.split(delimiter).map(v => v.trim());
        const newValues = splitValues.filter(v => validateValue(v, splitValues));

        if (newValues.length > 0) {
            onChange([...values, ...newValues]);
        }

        setInputValue('');
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Backspace' && inputValue === '' && values.length > 0) {
            onChange([...values].slice(0, -1));
            event.preventDefault();
            return;
        }

        if (event.key === 'Enter' && inputValue) {
            event.preventDefault();
        }
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (inputValue) {
            validateAndAddValue(inputValue.trim());
        }
        setInputValue('');
        onBlur?.(e);
    };

    return (
        <Box>
            <Autocomplete
                multiple
                freeSolo
                options={[]}
                disableClearable
                disabled={disabled}
                value={values}
                inputValue={inputValue}
                onInputChange={handleInputValueChange}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Tag key={key} {...tagProps} label={option} showDelete={showDelete} />;
                    })
                }
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <StyledTextField
                        {...params}
                        error={!!error}
                        label={label}
                        placeholder={placeholder ? placeholder : label}
                        required={required}
                        onChange={handleInputChange}
                        onBlur={handleOnBlur}
                    />
                )}
            />
            {error && (
                <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                    {error}
                </Typography>
            )}
        </Box>
    );
};
